/**
 * iOS "Add to Home Screen" prompt.
 *
 * iOS Safari has zero auto-install UI — the user must MANUALLY pick Share →
 * "Add to Home Screen". Without web-push fallback on Android Chrome, iOS
 * Safari users miss every notification: license-renewal reminders, messages,
 * system alerts. This prompt nudges them once: explains the steps with a
 * visual cue, then never shows again (until the user dismisses + explicitly
 * un-dismisses).
 *
 * Shows ONLY when ALL of these hold:
 *   - Running on iOS (iPhone / iPad / iPod)
 *   - In Safari (other in-app browsers like Facebook / Chrome on iOS can't
 *     install PWAs, and we don't want to mislead users)
 *   - NOT already running standalone (already installed)
 *   - User hasn't dismissed in the last 14 days
 *
 * Triggers `init()` from bootstrap, fires the modal after a short delay so
 * the home screen renders first.
 */

import { createLogger } from '../../core/logger.js';
import * as bus from '../../core/eventBus.js';

const log = createLogger('pwa-install');

const DISMISS_KEY = 'c2c_pwa_install_dismissed_at';
/** How long after a permanent "תודה" dismissal to stay silent — 2 weeks. */
const RESHOW_AFTER_MS = 14 * 24 * 60 * 60 * 1000;
/** Delay before showing — let the home feed render first. */
const SHOW_DELAY_MS = 4000;

let scheduled = false;
/** Set true when the user picks "אחר כך" — stays silent for the rest of THIS
 *  session (this JS context), then resets on the next PWA launch / reload. */
let dismissedThisSession = false;

/** Idempotent — safe to call from bootstrap.
 *
 *  The prompt does NOT fire on bootstrap; it fires only after the user is
 *  logged in (`auth:ready` event). Two reasons:
 *   1. Showing the install prompt before login is confusing — the user
 *      doesn't know what they're installing yet.
 *   2. The install-session bridge needs a logged-in user to mint the
 *      token, so firing the prompt before login wastes the bridge.
 */
export function init() {
  if (scheduled) return;
  scheduled = true;

  // `?show_install=1` in the URL bypasses dismissal + auth-required.
  // Useful for QA when "תודה" silenced the prompt for 14 days.
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('show_install') === '1') {
      try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
      log.info('install-prompt force-shown via ?show_install=1');
      setTimeout(() => { void prepareAndRender(); }, 500);
      return;
    }
  } catch { /* ignore */ }

  // Standard path: wait for the user to be authenticated before scheduling.
  // The bus fires `auth:ready` after validateSession succeeds OR after a
  // successful registerOrLogin (cold login).
  bus.on('auth:ready', () => {
    if (!shouldPrompt()) {
      log.info('install-prompt skipped post-auth (shouldPrompt=false)');
      return;
    }
    log.info('install-prompt scheduled (auth:ready received)');
    setTimeout(() => {
      if (shouldPrompt()) void prepareAndRender();
    }, SHOW_DELAY_MS);
  });
}

/**
 * Show the install prompt. No token minting — iOS 18+ isolates PWA
 * storage fully from Safari (verified empirically), so any bridge token
 * we'd mint never reaches the PWA. The user does one OTP login in the
 * newly-installed PWA, like any native iOS app, and subsequent opens
 * skip OTP via `trustedDevices`.
 */
async function prepareAndRender() {
  renderModal();
}

// ====================================================================
// internals
// ====================================================================

function isiOS() {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) && !/Android/i.test(ua);
}

function isStandalone() {
  // iOS exposes this as `navigator.standalone`; Android/Desktop expose the
  // display-mode media query.
  const navAny = /** @type {any} */ (navigator);
  if (navAny.standalone === true) return true;
  try {
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
  } catch { /* ignore */ }
  return false;
}

function recentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < RESHOW_AFTER_MS;
  } catch {
    return false;
  }
}

function shouldPrompt() {
  if (!isiOS()) return false;
  // Show on Safari iOS AND Chrome/Firefox/Edge iOS — they ALL support
  // Share → Add to Home Screen, with near-identical flows.
  if (isStandalone()) return false;
  if (recentlyDismissed()) return false;
  if (dismissedThisSession) return false;
  return true;
}

function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); }
  catch { /* ignore */ }
}

function renderModal() {
  const root = document.createElement('div');
  root.id = 'pwa-install-modal';
  root.innerHTML = `
    <div class="pwa-install-backdrop" role="dialog" aria-modal="true">
      <div class="pwa-install-card">
        <button class="pwa-install-close" aria-label="סגור">✕</button>

        <!-- View 1: pitch -->
        <div class="pwa-install-view" data-view="pitch">
          <div class="pwa-install-header">
            <div class="pwa-install-icon">
              <img src="/icons/icon-512.png" alt="AutoMe" />
            </div>
            <h2>התקן את AutoMe על המסך הראשי</h2>
            <p>חוויה מהירה יותר + התראות בזמן אמת על הודעות חדשות ועל תוקף הרישיון.</p>
          </div>
          <div class="pwa-install-actions">
            <button class="pwa-install-yes" type="button">אני רוצה להתקין</button>
          </div>
          <div class="pwa-install-actions pwa-install-actions-dismiss">
            <button class="pwa-install-later" type="button">אחר כך</button>
            <button class="pwa-install-thanks" type="button">תודה, לא תודה</button>
          </div>
        </div>

        <!-- View 2: instructions — same flow in Safari and Chrome iOS. -->
        <div class="pwa-install-view" data-view="instructions" style="display:none;">
          <h2 class="pwa-install-instructions-title">איך להתקין:</h2>
          <ol class="pwa-install-steps">
            <li>
              <span class="step-num">1</span>
              <span class="step-text">לחץ על כפתור <strong>שיתוף</strong> בתחתית הדפדפן
                <svg class="share-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 3l-4 4h3v8h2V7h3l-4-4zM5 19v-9h4v2H7v7h10v-7h-2v-2h4v9H5z"/>
                </svg>
              </span>
            </li>
            <li>
              <span class="step-num">2</span>
              <span class="step-text">גלול ובחר <strong>"הוסף למסך הבית"</strong></span>
            </li>
            <li>
              <span class="step-num">3</span>
              <span class="step-text">לחץ על <strong>הוסף</strong> בפינה</span>
            </li>
          </ol>
          <div class="pwa-install-actions">
            <button class="pwa-install-done" type="button">סיימתי</button>
          </div>
        </div>
      </div>
    </div>
  `;
  injectStyles();
  document.body.appendChild(root);

  /** "תודה, לא תודה" — permanent dismiss for RESHOW_AFTER_MS (14 days). */
  const dismissForever = () => {
    markDismissed();
    dismissedThisSession = true;
    root.remove();
  };
  /** "אחר כך" / X / backdrop / "סיימתי" — session-only. Returns next launch. */
  const dismissForSession = () => {
    dismissedThisSession = true;
    root.remove();
  };

  /** "אני רוצה להתקין" — just show the instructions view.
   *
   *  Previously this also minted an install-session token to bridge the
   *  Safari → PWA login. That bridge has been removed on iOS: empirical
   *  testing on iOS 18.7 confirmed Apple fully isolates PWA storage
   *  (localStorage, IndexedDB, URL query string ALL stripped), so the
   *  token never reaches the PWA. The user does one OTP login in the
   *  installed PWA — same as any native iOS app — and subsequent opens
   *  skip OTP automatically via `trustedDevices`. */
  const onInstallClick = () => {
    showView('instructions');
  };

  const showView = (/** @type {'pitch' | 'instructions'} */ view) => {
    root.querySelectorAll('.pwa-install-view').forEach((el) => {
      /** @type {HTMLElement} */ (el).style.display =
        el.getAttribute('data-view') === view ? '' : 'none';
    });
  };

  root.querySelector('.pwa-install-yes')?.addEventListener('click', () => { void onInstallClick(); });
  root.querySelector('.pwa-install-thanks')?.addEventListener('click', dismissForever);
  root.querySelector('.pwa-install-later')?.addEventListener('click', dismissForSession);
  root.querySelector('.pwa-install-done')?.addEventListener('click', dismissForSession);
  root.querySelector('.pwa-install-close')?.addEventListener('click', dismissForSession);
  // Click outside the card → session-only dismiss.
  root.querySelector('.pwa-install-backdrop')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) dismissForSession();
  });
}

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    #pwa-install-modal .pwa-install-backdrop {
      position: fixed; inset: 0; z-index: 99998;
      background: rgba(15, 23, 42, 0.6);
      display: flex; align-items: flex-end; justify-content: center;
      padding: 16px;
      animation: pwa-fade-in 0.25s ease-out;
    }
    @keyframes pwa-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    #pwa-install-modal .pwa-install-card {
      background: #ffffff; border-radius: 22px;
      width: 100%; max-width: 380px;
      padding: 24px 22px 20px;
      direction: rtl; text-align: center;
      box-shadow: 0 -8px 32px rgba(15, 23, 42, 0.25);
      position: relative;
      animation: pwa-slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    }
    @keyframes pwa-slide-up {
      from { transform: translateY(40px); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }
    #pwa-install-modal .pwa-install-close {
      position: absolute; top: 14px; left: 14px;
      width: 32px; height: 32px; border-radius: 50%;
      border: 0; background: rgba(15, 23, 42, 0.06);
      color: #64748b; font-size: 16px; font-weight: 700;
      cursor: pointer; display: flex; align-items: center; justify-content: center;
    }
    #pwa-install-modal .pwa-install-header {
      margin-bottom: 18px;
    }
    #pwa-install-modal .pwa-install-icon {
      width: 76px; height: 76px; margin: 0 auto 12px;
      border-radius: 18px; overflow: hidden;
      background: #f1f5f9;
      box-shadow: 0 6px 16px rgba(45, 168, 138, 0.18);
    }
    #pwa-install-modal .pwa-install-icon img {
      width: 100%; height: 100%; object-fit: cover;
    }
    #pwa-install-modal h2 {
      margin: 0 0 6px; font-size: 19px; font-weight: 700; color: #0f172a;
    }
    #pwa-install-modal .pwa-install-header p {
      margin: 0; color: #475569; font-size: 14px; line-height: 1.5;
    }
    #pwa-install-modal .pwa-install-steps {
      list-style: none; padding: 0; margin: 18px 0;
      display: flex; flex-direction: column; gap: 12px;
      text-align: start;
    }
    #pwa-install-modal .pwa-install-steps li {
      display: flex; align-items: center; gap: 12px;
      background: #f8fafc; border-radius: 14px;
      padding: 12px 14px;
    }
    #pwa-install-modal .step-num {
      flex-shrink: 0;
      width: 28px; height: 28px; border-radius: 50%;
      background: #2DA88A; color: #fff;
      display: flex; align-items: center; justify-content: center;
      font-size: 14px; font-weight: 700;
    }
    #pwa-install-modal .step-text {
      flex: 1; font-size: 14px; color: #334155; line-height: 1.5;
      display: inline-flex; align-items: center; gap: 6px;
      flex-wrap: wrap;
    }
    #pwa-install-modal .step-text strong { color: #0f172a; font-weight: 700; }
    #pwa-install-modal .share-icon {
      width: 20px; height: 20px; fill: #2DA88A;
      display: inline-block; vertical-align: middle;
    }
    #pwa-install-modal .pwa-install-actions {
      display: flex; gap: 10px; margin-top: 4px;
    }
    #pwa-install-modal .pwa-install-actions-dismiss {
      margin-top: 10px;
    }
    #pwa-install-modal .pwa-install-yes,
    #pwa-install-modal .pwa-install-done,
    #pwa-install-modal .pwa-install-thanks,
    #pwa-install-modal .pwa-install-later {
      flex: 1; padding: 12px; border: 0; border-radius: 12px;
      font-size: 14px; font-weight: 700; cursor: pointer;
      font-family: inherit;
      transition: background 0.15s, transform 0.1s, opacity 0.15s;
    }
    #pwa-install-modal .pwa-install-yes:disabled,
    #pwa-install-modal .pwa-install-done:disabled { opacity: 0.7; cursor: wait; }
    /* "אני רוצה להתקין" — primary CTA: brand gradient, large. */
    #pwa-install-modal .pwa-install-yes,
    #pwa-install-modal .pwa-install-done {
      background: linear-gradient(135deg, #14b8a6 0%, #6366f1 100%);
      color: #fff;
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
      font-size: 15px;
    }
    #pwa-install-modal .pwa-install-yes:active,
    #pwa-install-modal .pwa-install-done:active { transform: scale(0.97); }
    /* "תודה, לא תודה" — tertiary: text-style, smallest. */
    #pwa-install-modal .pwa-install-thanks {
      background: transparent; color: #64748b;
      font-weight: 600; font-size: 13px;
    }
    #pwa-install-modal .pwa-install-thanks:hover { background: #f1f5f9; color: #334155; }
    /* "אחר כך" — secondary: neutral, remind-me-later. */
    #pwa-install-modal .pwa-install-later {
      background: #f1f5f9; color: #475569;
      font-size: 13px;
    }
    #pwa-install-modal .pwa-install-later:hover { background: #e2e8f0; }
    #pwa-install-modal .pwa-install-later:active { transform: scale(0.97); }
    /* "איך להתקין:" — header for the instructions view. */
    #pwa-install-modal .pwa-install-instructions-title {
      margin: 0 0 12px; font-size: 17px; font-weight: 700;
      color: #0f172a; text-align: center;
    }
  `;
  document.head.appendChild(style);
}
